import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import handleAsyncThunk from "../handleAsyncThunk";
import authApi from "../../services/authApi.js";
import { storage } from "../../config/storage";
import { logMeIn, logMeOut } from "./userSlice.js";
import { showErrorSnackbar, showSuccessSnackbar } from "./snackbarSlice.js";
import { resetPublicState } from "./publicSlice.js";
 
// LOGIN API
export const login = createAsyncThunk(
    "/login",
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.login(data);
            if (response.status === 0) {
                if(response?.data?.token){    
                    // Log me in and store the user data in store and local storage
                    dispatch(logMeIn({ user: response?.data, token: response?.data?.token }));
                }

                // show success toast message
                dispatch(showSuccessSnackbar({ title: response?.data?.message }));
                return response?.data; // Return the response data on success
            } else {
                // show error toast message
                dispatch(showErrorSnackbar({ title: response?.message }));
                return rejectWithValue(response?.message); // Reject with error message
            }
        } catch (error) {
            console.log(error);
            dispatch(showErrorSnackbar({ title: error?.message }));
            return rejectWithValue(error.message); // Handle unexpected errors
        }
    }
);

//LOGOUT API
export const logout = createAsyncThunk(
    "/logout",
    async (_, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.logout();
            if (response.status === 0) {
                // logMeOut removes the user data from store and local storage
                dispatch(logMeOut());

                // Reset Public-State
                dispatch(resetPublicState());

                // show success toast
                dispatch(showSuccessSnackbar({ title: response?.data?.message }));
                return response?.data; // Return the response data on success
            } else {
                // show error toast
                dispatch(showErrorSnackbar({ title: response?.message }));
                return rejectWithValue(response?.message); // Reject with error message
            }
        } catch (error) {
            console.log(error);
            dispatch(showErrorSnackbar({ title: error?.message }));
            return rejectWithValue(error.message); // Handle unexpected errors
        }
    }
);
 
// SIGNUP API
export const signUp = createAsyncThunk(
    "/signUp",
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.signUp(data);
            if (response.status === 0) {
                // show success toast
                dispatch(showSuccessSnackbar({ title: response?.data?.message }));
                return response?.data; // Return the response data on success
            } else {
                // show error toast
                dispatch(showErrorSnackbar({ title: response?.message }));
                return rejectWithValue(response?.message); // Reject with error message
            }
        } catch (error) {
            console.log(error);
            dispatch(showErrorSnackbar({ title: error?.message }));
            return rejectWithValue(error.message); // Handle unexpected errors
        }
    }
);

// SIGNUP API WITHOUT OTP
export const signUpWithoutOtp = createAsyncThunk(
    "/signUpWithoutOtp",
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.signUpWithoutOtp(data);
            if (response.status === 0) {
                // show success toast
                // dispatch(showSuccessSnackbar({ title: response?.data?.message }));
                return response?.data; // Return the response data on success
            } else {
                // show error toast
                dispatch(showErrorSnackbar({ title: response?.message }));
                return rejectWithValue(response?.message); // Reject with error message
            }
        } catch (error) {
            console.log(error);
            dispatch(showErrorSnackbar({ title: error?.message }));
            return rejectWithValue(error.message); // Handle unexpected errors
        }
    }
);
 
// VERIFY OTP API
export const verifyOTP = createAsyncThunk(
    "/verifyOTP",
    async ({ data, onCompleteNewAccount }, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.verifyOTP(data);
            if (response.status === 0) {
                
                if(response?.data?.newAccount){
                    // redirect to create password page if new account
                    onCompleteNewAccount(true)
                }else{
                    // log me in if not a new account
                    await dispatch(logMeIn({ user: response?.data, token: response?.data?.token }));
                    // redirect to select-sport page
                    onCompleteNewAccount(false)
                }

                // show success toast
                dispatch(showSuccessSnackbar({ title: response?.data?.message }));
                return response?.data; // Return the response data on success 
            } else {
                storage.destroyFromLocalStorage.token();
                // show error message
                dispatch(showErrorSnackbar({ title: response?.message }));
                return rejectWithValue(response?.message); // Reject with error message
            }
        } catch (error) {
            console.log(error);
            storage.destroyFromLocalStorage.token();
            dispatch(showErrorSnackbar({ title: error?.message }));
            return rejectWithValue(error.message); // Handle unexpected errors
        }
    }
);
 
// RESEND OTP API
export const resendOTP = createAsyncThunk(
    "/resendOTP",
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.resendOTP(data);
 
            if (response.status === 0) {              
                // show success toast
                dispatch(showSuccessSnackbar({ title: response?.data?.message }));
                return response?.data; // Return the response data on success
            } else {
                // show error toast
                dispatch(showErrorSnackbar({ title: response?.message }));
                return rejectWithValue(response?.message); // Reject with error message
            }
        } catch (error) {
            console.log(error);
            dispatch(showErrorSnackbar({ title: error?.message }));
            return rejectWithValue(error.message); // Handle unexpected errors
        }
    }
);
 
// CREATE PASSWORD API
export const createPassword = createAsyncThunk(
    "/create-password",
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.createPassword(data);
 
            if (response.status === 0) {
                // Log me in and store the user data in store and local storage
                dispatch(logMeIn({ user: response?.data, token: response?.data?.token }));
                
                // show success toast
                dispatch(showSuccessSnackbar({ title: response?.data?.message }));
                return response.data; // Return the response data on success message
            } else {
                // show error toast
                dispatch(showErrorSnackbar({ title: response?.message }));
                return rejectWithValue(response?.message); // Reject with error message
            }
        } catch (error) {
            console.log(error);
            dispatch(showErrorSnackbar({ title: error?.message }));
            return rejectWithValue(error.message); // Handle unexpected errors
        }
    }
);

// FORGOT PASSWORD API
export const forgotPassword = createAsyncThunk(
    "/forgot-password",
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.forgotPassword(data);
 
            if (response.status === 0) {
                // show success toast
                dispatch(showSuccessSnackbar({ title: response?.data?.message }));
                return response.data; // Return the response data on success message
            } else {
                // show error toast
                dispatch(showErrorSnackbar({ title: response?.message }));
                return rejectWithValue(response?.message); // Reject with error message
            }
        } catch (error) {
            console.log(error);
            dispatch(showErrorSnackbar({ title: error?.message }));
            return rejectWithValue(error.message); // Handle unexpected errors
        }
    }
);

// RESET PASSWORD API
export const resetPassword = createAsyncThunk(
    "/reset-password",
    async (data, { rejectWithValue, dispatch }) => {
        try {
            const response = await authApi.resetPassword(data);
 
            if (response.status === 0) {
                // show success toast
                dispatch(showSuccessSnackbar({ title: response?.data?.message }));
                return response.data; // Return the response data on success
            } else {
                // show error toast
                dispatch(showErrorSnackbar({ title: response?.message }));
                return rejectWithValue(response?.message); // Reject with error message
            }
        } catch (error) {
            console.log(error);
            dispatch(showErrorSnackbar({ title: error?.message }));
            return rejectWithValue(error.message); // Handle unexpected errors
        }
    }
);
 
 
const authSlice = createSlice({
    name: "auth",
    initialState: {
        isLoading: false,
        resendLoading: false,
        apiData: null,
        loginError: null,
        signupError: null,
        otpError: null,
        createPasswordError: null
    },
 
    extraReducers: (builder) => {
        handleAsyncThunk(builder, login, "apiData", "loginError");
        handleAsyncThunk(builder, signUp, "apiData", "signupError");
        handleAsyncThunk(builder, signUpWithoutOtp, "apiData", "signupWithoutOtpError, signUpWithoutOtpLoading");
        handleAsyncThunk(builder, resendOTP, "apiData", "resendOTPError", "resendLoading");
        handleAsyncThunk(builder, verifyOTP, "apiData", "otpError");
        handleAsyncThunk(builder, createPassword, "apiData", "createPasswordError");
        handleAsyncThunk(builder, resetPassword, "apiData", "resetPasswordError");
        handleAsyncThunk(builder, forgotPassword, "apiData", "forgotPasswordError");
        handleAsyncThunk(builder, logout, "apiData", "forgotPasswordError", "loggingOut");
    },
});
 
export default authSlice.reducer;